import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'

import ShopeeProductSelectSearch from '../../../components/SelectSearch/ShopeeProductSelectSearch'
import { EPLATFORM } from '../../../constants/platform'
import useAxios from '../../../hooks/useAxios'
import { IPlatformLink, IUsedPlatformLink } from '../../../types/product'
import {
  buildUsedLinkMap,
  platformLinkKey,
} from '../../../utils/platform-link.util'
import TiktokProductPicker from './TiktokProductPicker'

interface Props {
  platform: string
  links: IPlatformLink[]
  onChange: (links: IPlatformLink[]) => void
  // ค่าที่ผูกไว้แบบเดิม (referrer.<platform>) — โชว์ให้รู้ว่ายังผูกอยู่ ไม่ใช่ยังไม่ผูก
  legacyPlatformId?: string
  // SKU ที่ผูกไปแล้วทั้งร้าน (ทุกสินค้า) — ของสินค้าตัวนี้เองถูกกรองออกด้วย objectId
  usedLinks?: IUsedPlatformLink[]
  currentProductObjectId?: string
}

// ตัวเลือกสินค้าของแต่ละ platform คืน field คนละชื่อ → ทำให้เป็นรูปเดียวก่อนเก็บ
type ISelectedSku = Pick<
  IPlatformLink,
  'shopId' | 'shopName' | 'platformId' | 'productName' | 'skuId' | 'sku'
>

// ที่มาของการผูก — ผูกเองกับผูกให้อัตโนมัติต้องแยกออกจากกัน เพราะตัวที่ระบบผูกให้
// ยังไม่มีชื่อสินค้า/ชื่อร้าน (webhook ไม่ได้ส่งมา) และอาจผูกผิดตัวได้
const LINKED_BY_LABEL: Record<string, { text: string; color: string }> = {
  lazy: { text: 'ผูกอัตโนมัติ', color: 'info.main' },
  migration: { text: 'ย้ายจากของเดิม', color: 'warning.main' },
}

// ศัพท์ของแต่ละ platform ต่างกัน — ใช้ชื่อจริงฝั่งนั้นเพื่อให้เทียบกับหน้าร้านได้
const PLATFORM_TERMS: Record<
  string,
  { name: string; id: string; skuId: string; sku: string }
> = {
  [EPLATFORM.TIKTOK]: {
    name: 'TikTok',
    id: 'product_id',
    skuId: 'sku_id',
    sku: 'seller_sku',
  },
  [EPLATFORM.SHOPEE]: {
    name: 'Shopee',
    id: 'item_id',
    skuId: 'model_id',
    sku: 'model_sku',
  },
}

// บัญชีร้านที่เชื่อมไว้ของแต่ละ platform — ใช้แปลง shopId เป็นชื่อร้าน
const ACCOUNTS_URL: Record<string, string> = {
  [EPLATFORM.TIKTOK]: '/tiktok/merchant/accounts',
  [EPLATFORM.SHOPEE]: '/shopee/merchant/accounts',
}

const defaultTerms = {
  name: 'marketplace',
  id: 'product_id',
  skuId: 'sku_id',
  sku: 'sku',
}

// sku_id ของ TikTok เป็นเลข 19 หลัก — ร้านเคยกรอกเลขนี้ลงช่อง seller_sku จนระบบหาสินค้าไม่เจอ
// (barcode ที่ใช้เป็น SKU ได้ยาวสุด 14 หลัก จึงไม่ชนกัน)
const LOOKS_LIKE_SKU_ID = /^\d{16,}$/

// ผูกสินค้าในระบบเข้ากับ SKU ฝั่ง marketplace ได้หลายรายการ
// (หลาย variant / หลายร้าน) — ระบบตัดสต๊อกหาสินค้าจาก sku_id ก่อน แล้วค่อยตกไป SKU
const PlatformLinksField = (props: Props) => {
  const {
    platform,
    links,
    onChange,
    legacyPlatformId,
    usedLinks = [],
    currentProductObjectId,
  } = props
  const [selected, setSelected] = useState<ISelectedSku | null>(null)
  const [manualPlatformId, setManualPlatformId] = useState<string>('')
  const [manualSku, setManualSku] = useState<string>('')
  const [error, setError] = useState<string>('')

  const terms = PLATFORM_TERMS[platform] ?? defaultTerms

  // link ที่ระบบผูกให้ / ย้ายมาจากของเดิมไม่มี shopName เก็บไว้ — ดึงชื่อจากบัญชีร้านแทน
  // (URL เดียวกับที่ตัวเลือกสินค้าใช้ SWR จึงไม่ยิงซ้ำ)
  const { data: accounts } = useAxios<{ shopId: string; sellerName: string }[]>(
    ACCOUNTS_URL[platform]
      ? { method: 'GET', url: ACCOUNTS_URL[platform] }
      : null,
  )
  const shopNameById = useMemo(
    () =>
      new Map(
        (accounts ?? []).map((account) => [account.shopId, account.sellerName]),
      ),
    [accounts],
  )
  const platformLinks = links.filter((link) => link.platform === platform)
  const otherLinks = links.filter((link) => link.platform !== platform)

  // ปิดรายการที่ผูกไปแล้ว — รวมของที่เพิ่งเพิ่มใน modal (ยังไม่บันทึก) และ
  // ปลดของที่เพิ่งลบออกให้เลือกใหม่ได้ทันที เพราะคิดจาก state ปัจจุบันไม่ใช่ข้อมูล server อย่างเดียว
  const usedKeys = buildUsedLinkMap(
    usedLinks.filter((link) => link.platform === platform),
    platformLinks,
    currentProductObjectId,
  )

  // ที่ผูกกับสินค้าตัวนี้เอง — แยกออกมาเพราะวิธีแก้ต่างกัน: ของตัวเองลบได้เองในลิสต์
  // ส่วนของสินค้าอื่นต้องไปปลดที่สินค้านั้น ข้อความจึงต้องไม่ปนกัน
  const ownKeys = buildUsedLinkMap([], platformLinks)

  const addLink = (link: IPlatformLink) => {
    // ช่องกรอกเองเลี่ยงรายการที่ปิดไว้ได้ — เช็กซ้ำที่นี่ด้วย
    const key = platformLinkKey(link)
    if (key && ownKeys.has(key)) {
      return setError('SKU นี้ผูกกับสินค้านี้อยู่แล้ว — ดูรายการด้านบน')
    }
    const usedBy = key ? usedKeys.get(key) : undefined
    if (usedBy) {
      return setError(`${usedBy} — ต้องปลดจากสินค้านั้นก่อนถึงจะผูกใหม่ได้`)
    }
    setError('')
    onChange([...otherLinks, ...platformLinks, { ...link, linkedBy: 'manual' }])
  }

  const handleAddSelected = () => {
    if (!selected) {
      return setError('กรุณาเลือกสินค้าก่อน')
    }
    addLink({ platform, ...selected })
    setSelected(null)
  }

  const handleAddManual = () => {
    if (!manualSku.trim()) {
      return setError('กรุณากรอก SKU')
    }
    if (LOOKS_LIKE_SKU_ID.test(manualSku.trim())) {
      return setError(
        `ตัวเลขนี้คือ ${terms.skuId} ไม่ใช่ชื่อ SKU — ช่องนี้ต้องใส่ชื่อ SKU ที่เป็นตัวหนังสือ เช่น "รังนก (CG) 2 ห่อ"`,
      )
    }
    addLink({
      platform,
      platformId: manualPlatformId.trim() || undefined,
      sku: manualSku.trim(),
    })
    setManualPlatformId('')
    setManualSku('')
  }

  const handleRemove = (index: number) => {
    const next = platformLinks.filter((_, current) => current !== index)
    onChange([...otherLinks, ...next])
    setError('')
  }

  return (
    <Stack spacing={2}>
      {platformLinks.length === 0 ? (
        legacyPlatformId ? (
          <Alert severity="info">
            ผูกไว้แบบเดิมที่ {terms.id} {legacyPlatformId} (ต้องมีรหัสสินค้า
            code ตรงกับ {terms.sku}) — ระบบจะย้ายมาผูกแบบใหม่ให้เองเมื่อมี
            คำสั่งซื้อเข้ามา หรือเลือกสินค้าด้านล่างเพื่อผูกทันที
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary">
            ยังไม่ได้ผูก SKU — ระบบจะพยายามผูกให้อัตโนมัติจากรหัสสินค้า (code)
            เมื่อมีคำสั่งซื้อเข้ามา
          </Typography>
        )
      ) : (
        platformLinks.map((link, index) => (
          <Box
            key={`${link.skuId || link.sku}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 2,
              py: 1,
            }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={500}>
                  {link.productName || 'ไม่ทราบชื่อสินค้า'}
                </Typography>
                {LINKED_BY_LABEL[link.linkedBy ?? ''] && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: LINKED_BY_LABEL[link.linkedBy ?? ''].color,
                      border: '1px solid currentColor',
                      borderRadius: 1,
                      px: 0.75,
                      lineHeight: 1.6,
                    }}
                  >
                    {LINKED_BY_LABEL[link.linkedBy ?? ''].text}
                  </Typography>
                )}
              </Stack>
              {/* pre-wrap: SKU บางตัวมีช่องว่างซ้อน ต้องเห็นตามจริงถึงจะเทียบกับหน้าร้านได้ */}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                SKU: {link.sku || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {terms.skuId}: {link.skuId || '-'} · {terms.id}:{' '}
                {link.platformId || '-'} · ร้าน:{' '}
                {link.shopName ||
                  shopNameById.get(link.shopId ?? '') ||
                  link.shopId ||
                  '-'}
              </Typography>
            </Box>
            <IconButton color="error" onClick={() => handleRemove(index)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ))
      )}

      {error && <Alert severity="warning">{error}</Alert>}

      {/* ใช้ Stack ไม่ใช้ Grid container — spacing ของ Grid ใส่ negative margin
          ทำให้แถวล้นขอบขวาของ Dialog ไม่ตรงกับหัวเรื่อง/ข้อความอื่น */}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', lg: 'flex-start' }}
      >
        <Box sx={{ flex: 1 }}>
          {platform === EPLATFORM.SHOPEE ? (
            <ShopeeProductSelectSearch
              label="เลือกสินค้า Shopee เพื่อผูก"
              value={selected?.skuId}
              usedKeys={usedKeys}
              onSelect={(option) => {
                setSelected(
                  option && {
                    shopId: option.shopId,
                    shopName: option.shopName,
                    platformId: option.itemId,
                    productName: option.itemName,
                    // สินค้าไม่มีตัวเลือก → ไม่มี model_id, ผูกที่ระดับ item แทน
                    skuId: option.modelId || undefined,
                    sku: option.sku?.trim(),
                  },
                )
                setError('')
              }}
            />
          ) : (
            <TiktokProductPicker
              label="เลือกสินค้า TikTok เพื่อผูก"
              value={selected?.skuId}
              usedKeys={usedKeys}
              onSelect={(option) => {
                setSelected(
                  option && {
                    shopId: option.shopId,
                    shopName: option.shopName,
                    platformId: option.productId,
                    productName: option.productName,
                    skuId: option.skuId,
                    sku: option.sellerSku?.trim(),
                  },
                )
                setError('')
              }}
            />
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddSelected}
          sx={{
            flexShrink: 0,
            width: { xs: '100%', lg: 180 },
            height: { lg: 56 },
          }}
        >
          เพิ่มการผูก
        </Button>
      </Stack>

      {/* กรอกเอง — กรณีสินค้าไม่ขึ้นในรายการ */}
      <Typography variant="caption" color="text.secondary">
        กรอกเอง — ใช้เมื่อหาสินค้าในรายการด้านบนไม่เจอ เช่น สินค้าถูกลบจาก{' '}
        {terms.name} ไปแล้ว
      </Typography>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems="stretch"
      >
        <TextField
          fullWidth
          label={`${terms.id} (กรอกเอง)`}
          value={manualPlatformId}
          onChange={(e) => setManualPlatformId(e.target.value)}
          helperText={`เลขสินค้าใน ${terms.name} (ตัวเลขยาว) — ไม่ใส่ก็ได้`}
        />
        <TextField
          fullWidth
          label={`${terms.sku} (กรอกเอง)`}
          value={manualSku}
          onChange={(e) => setManualSku(e.target.value)}
          helperText={`ชื่อ SKU ที่ร้านตั้งใน ${terms.name} เป็นตัวหนังสือ เช่น "รังนก (CG) 2 ห่อ" ไม่ใช่ตัวเลขยาว ๆ — คัดลอกมาวางจะดีที่สุด เพราะเว้นวรรคต้องตรงเป๊ะ`}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddManual}
          sx={{
            flexShrink: 0,
            width: { xs: '100%', lg: 180 },
            height: { lg: 56 },
          }}
        >
          เพิ่มแบบกรอกเอง
        </Button>
      </Stack>
    </Stack>
  )
}

export default PlatformLinksField
